import { NavLink } from 'react-router-dom';
import { ChevronsUpDown, Lock, LogOut, Monitor, Moon, PawPrint, Sun } from 'lucide-react';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
} from '@/components/ui/sidebar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { NAV_GROUPS } from '@/components/nav-items';
import { use_admin_stats } from '@/features/stats/hooks';
import { use_auth_store, use_is_super_admin } from '@/lib/auth-store';
import { use_theme_store, type ThemeChoice } from '@/lib/theme-store';
import { cn } from '@/lib/utils';

/**
 * Sidebar.
 *
 * Two substantive changes beyond the restyle:
 *
 *  - Queue counts render as badges on the nav items that own them. The
 *    dashboard already knew that eleven vendors were waiting on KYC; the
 *    sidebar — the thing actually on screen while you work — did not, so you
 *    had to go back to the dashboard to find out whether anything had arrived.
 *
 *  - The footer is an account menu rather than a static block plus a bare
 *    "Sign out" row. Sign-out sitting in the nav list, one pixel from
 *    "Admins", is a destructive action in a navigation position.
 */
export function AppSidebar() {
  const user = use_auth_store((s) => s.user);
  const clear = use_auth_store((s) => s.clear);
  const is_super_admin = use_is_super_admin();
  const theme = use_theme_store((s) => s.choice);
  const set_theme = use_theme_store((s) => s.set_choice);

  // Same query key as the dashboard, so this is the dashboard's already-cached
  // and already-polling result rather than a second request.
  const { data: stats } = use_admin_stats();

  // Drop super-admin-only entries — and any group left empty by that — rather
  // than rendering a nav item that would just bounce back to the dashboard.
  const visible_groups = NAV_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((i) => !i.hidden && (!i.super_admin_only || is_super_admin)),
  })).filter((g) => g.items.length > 0);

  const initials = (user?.first_name?.[0] ?? user?.email?.[0] ?? 'A').toUpperCase();
  const full_name = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || 'Admin';

  const THEME_OPTIONS: { value: ThemeChoice; label: string; icon: typeof Sun }[] = [
    { value: 'light', label: 'Light', icon: Sun },
    { value: 'dark', label: 'Dark', icon: Moon },
    { value: 'system', label: 'System', icon: Monitor },
  ];

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <div className="flex items-center gap-2.5 px-1.5 py-1.5">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-control accent-gradient text-white shadow-panel">
            <PawPrint className="h-4 w-4" />
          </div>
          <div className="flex min-w-0 flex-col group-data-[collapsible=icon]:hidden">
            <span className="truncate font-heading text-sm font-bold leading-tight">
              Pet Market
            </span>
            <span className="truncate text-micro text-muted-foreground">Admin Console</span>
          </div>
        </div>
      </SidebarHeader>
      <SidebarSeparator />

      <SidebarContent className="thin-scroll">
        {visible_groups.map((group) => (
          <SidebarGroup key={group.label}>
            <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
            <SidebarMenu>
              {group.items.map((item) => {
                const count = item.badge ? (stats?.queues[item.badge] ?? 0) : 0;
                if (item.locked) {
                  return (
                    <SidebarMenuItem key={item.to}>
                      <SidebarMenuButton
                        disabled
                        aria-disabled
                        tooltip={`${item.title} — coming soon`}
                        data-testid={`nav-locked-${item.title.toLowerCase()}`}
                        className="cursor-not-allowed opacity-60"
                      >
                        <item.icon className="h-4 w-4" />
                        <span>{item.title}</span>
                        <Lock className="ml-auto h-3.5 w-3.5 text-muted-foreground group-data-[collapsible=icon]:hidden" />
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                }
                return (
                  <SidebarMenuItem key={item.to}>
                    <NavLink to={item.to} end>
                      {({ isActive }) => (
                        <SidebarMenuButton
                          asChild
                          isActive={isActive}
                          tooltip={count > 0 ? `${item.title} — ${count} waiting` : item.title}
                          data-testid={`nav-${item.title.toLowerCase()}`}
                        >
                          <span>
                            <item.icon className="h-4 w-4" />
                            <span className="truncate">{item.title}</span>
                            {count > 0 && (
                              <span
                                className={cn(
                                  'ml-auto tabular rounded-pill px-1.5 py-px text-micro font-bold group-data-[collapsible=icon]:hidden',
                                  isActive
                                    ? 'bg-primary text-primary-foreground'
                                    : 'bg-primary/12 text-primary',
                                )}
                                data-testid={`nav-badge-${item.badge}`}
                              >
                                {count > 99 ? '99+' : count}
                              </span>
                            )}
                          </span>
                        </SidebarMenuButton>
                      )}
                    </NavLink>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarSeparator />
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  className="h-auto py-1.5"
                  tooltip={user?.email ?? 'Account'}
                  data-testid="account-menu"
                >
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-bold text-secondary-foreground">
                    {initials}
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col text-left">
                    <span className="truncate text-data font-semibold">{full_name}</span>
                    <span className="truncate text-micro text-muted-foreground">{user?.email}</span>
                  </span>
                  <ChevronsUpDown className="ml-auto h-3.5 w-3.5 text-muted-foreground group-data-[collapsible=icon]:hidden" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="top" align="start" className="min-w-[14rem]">
                <DropdownMenuLabel>
                  <span className="block truncate normal-case tracking-normal text-foreground">
                    {full_name}
                  </span>
                  <span className="block truncate font-normal normal-case tracking-normal text-muted-foreground">
                    {is_super_admin ? 'Super admin' : 'Admin'}
                  </span>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Appearance</DropdownMenuLabel>
                {THEME_OPTIONS.map((o) => (
                  <DropdownMenuItem key={o.value} onSelect={() => set_theme(o.value)}>
                    <o.icon className="h-4 w-4" />
                    {o.label}
                    {theme === o.value && (
                      <span className="ml-auto text-micro font-bold text-primary">Active</span>
                    )}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem tone="destructive" onSelect={clear} data-testid="sign-out">
                  <LogOut className="h-4 w-4" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
